import React, { useState } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, IconButton, Collapse, Tooltip
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Surcharge Report (Platform B) result body — rendered by SettlementReports when reportType === 'surcharge'.
// Data shape: see generateSurchargeReport in backend/functions/businesses/index.js.

const money = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (n) => (parseInt(n, 10) || 0).toLocaleString();
const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function SummaryCard({ label, value, color, emphasis, bg }) {
  return (
    <Grid item xs={6} sm={3} md={2.4}>
      <Paper elevation={emphasis ? 3 : 1} sx={{ p: 2, textAlign: 'center', backgroundColor: bg || '#f5f5f5',
        ...(emphasis ? { borderColor: 'success.main', borderWidth: 2, borderStyle: 'solid' } : {}) }}>
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
        <Typography variant="h6" color={color || 'text.primary'} fontWeight={emphasis ? 'bold' : undefined}>{value}</Typography>
      </Paper>
    </Grid>
  );
}

export default function SurchargeReportBody({ data }) {
  const [open, setOpen] = useState({});
  const s = data?.summary || {};
  const locations = data?.locations || [];
  const skipped = data?.skippedLocations || [];
  const toggle = (name) => setOpen(prev => ({ ...prev, [name]: !prev[name] }));

  const exportCsv = () => {
    const header = ['Location', 'Platform', 'Lanes', 'Attempts', 'Declined / No Cash', 'Surcharged Txns', 'Surcharge Collected', 'Avg Surcharge',
      'Payee', 'Account', 'Rate', 'Payee Txns', 'Owed'];
    const lines = [header.join(',')];
    locations.forEach(loc => {
      const base = [loc.name, 'Platform B', loc.terminalCount, loc.attempts, loc.declined, loc.surchargedTxns, loc.surchargeCollected.toFixed(2), loc.avgSurcharge.toFixed(2)];
      if (loc.splits.length === 0) lines.push([...base, '', '', '', '', ''].map(csvCell).join(','));
      loc.splits.forEach(sp => lines.push([...base, sp.payee, `...${sp.accountLast4}`, sp.rate.toFixed(2), sp.txns, sp.owed.toFixed(2)].map(csvCell).join(',')));
      lines.push([...base, 'UNALLOCATED (collected - splits)', '', '', '', loc.unallocated.toFixed(2)].map(csvCell).join(','));
    });
    lines.push(['TOTAL', 'Platform B', s.totalTerminals, s.totalAttempts, s.totalDeclined, s.totalTransactions, (s.totalSurcharge || 0).toFixed(2), '',
      'All payees', '', '', '', (s.totalOwed || 0).toFixed(2)].map(csvCell).join(','));
    lines.push(['TOTAL', 'Platform B', '', '', '', '', '', '', 'UNALLOCATED', '', '', '', (s.totalUnallocated || 0).toFixed(2)].map(csvCell).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `surcharge_report_${data.startDate}_to_${data.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>{data.basisNote}</Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <SummaryCard label="Locations" value={int(s.totalLocations)} />
        <SummaryCard label="Lanes" value={int(s.totalTerminals)} />
        <SummaryCard label="Attempts" value={int(s.totalAttempts)} />
        <SummaryCard label="Declined / No Cash" value={int(s.totalDeclined)} color="warning.main" bg="#fff3e0" />
        <SummaryCard label="Surcharged Txns" value={int(s.totalTransactions)} emphasis bg="#e8f5e9" color="success.main" />
        <SummaryCard label="Surcharge Collected" value={money(s.totalSurcharge)} emphasis bg="#e8f5e9" color="success.main" />
        <SummaryCard label="Owed to Payees" value={money(s.totalOwed)} bg="#e3f2fd" color="primary" />
        <SummaryCard label="Unallocated" value={money(s.totalUnallocated)} bg="#e3f2fd" color={s.totalUnallocated < 0 ? 'error' : 'primary'} />
        <SummaryCard label="Dispensed" value={money(s.totalDispensed)} />
      </Grid>

      {s.byPayee && s.byPayee.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Owed by payee (all selected locations)</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {s.byPayee.map(p => (
              <Chip key={`${p.payee}|${p.accountLast4}`} size="small" variant="outlined"
                label={`${p.payee} (…${p.accountLast4}): ${int(p.txns)} txns · ${money(p.owed)} · ${p.locations} loc`} />
            ))}
          </Box>
        </Paper>
      )}

      {skipped.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Not included ({skipped.length}): {skipped.map(x => x.location).join(', ')} — {skipped[0].reason}
        </Alert>
      )}

      {locations.length > 0 && (
        <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
          <Button variant="contained" color="success" startIcon={<GetAppIcon />} onClick={exportCsv}>Export Surcharge Report</Button>
        </Box>
      )}

      <Typography variant="h6" gutterBottom>Location Details</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Location</TableCell>
            <TableCell align="right">Lanes</TableCell>
            <TableCell align="right">Attempts</TableCell>
            <TableCell align="right">Declined / No Cash</TableCell>
            <TableCell align="right">Surcharged</TableCell>
            <TableCell align="right">Surcharge Collected</TableCell>
            <TableCell align="right">Avg</TableCell>
            <TableCell align="right">Owed to Payees</TableCell>
            <TableCell align="right">Unallocated</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {locations.map(loc => (
            <React.Fragment key={loc.name}>
              <TableRow hover sx={loc.splitsExceedCollected ? { backgroundColor: '#fff8e1' } : undefined}>
                <TableCell padding="checkbox">
                  <IconButton size="small" onClick={() => toggle(loc.name)} aria-label="toggle split lines">
                    {open[loc.name] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {loc.name}
                  {loc.splitsExceedCollected && (
                    <Tooltip title="Configured splits promise more than the surcharge collected">
                      <Chip size="small" color="warning" variant="outlined" label="splits exceed collected" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">{int(loc.terminalCount)}</TableCell>
                <TableCell align="right">{int(loc.attempts)}</TableCell>
                <TableCell align="right" sx={{ color: 'warning.main' }}>{int(loc.declined)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{int(loc.surchargedTxns)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{money(loc.surchargeCollected)}</TableCell>
                <TableCell align="right">{money(loc.avgSurcharge)}</TableCell>
                <TableCell align="right">{money(loc.totalOwed)}</TableCell>
                <TableCell align="right" sx={{ color: loc.unallocated < 0 ? 'error.main' : undefined }}>{money(loc.unallocated)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                  <Collapse in={!!open[loc.name]} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 6, pr: 2, py: 1, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary">
                        Lanes: {loc.lanes.map(l => `${l.laneName} (${l.terminalNumber}: ${l.ghTerminals.join(', ')})`).join(' · ')}
                      </Typography>
                      <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Payee</TableCell>
                            <TableCell>Account</TableCell>
                            <TableCell align="right">Rate / txn</TableCell>
                            <TableCell align="right">Surcharged Txns</TableCell>
                            <TableCell align="right">Owed</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loc.splits.map(sp => (
                            <TableRow key={`${sp.payee}|${sp.accountLast4}`}>
                              <TableCell>{sp.payee}</TableCell>
                              <TableCell>…{sp.accountLast4}</TableCell>
                              <TableCell align="right">{money(sp.rate)}{sp.rateMixed ? ' (mixed)' : ''}</TableCell>
                              <TableCell align="right">{int(sp.txns)}</TableCell>
                              <TableCell align="right">{money(sp.owed)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell colSpan={4} align="right" sx={{ fontStyle: 'italic' }}>Unallocated (collected − splits)</TableCell>
                            <TableCell align="right" sx={{ fontStyle: 'italic' }}>{money(loc.unallocated)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
          <TableRow>
            <TableCell />
            <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{int(s.totalTerminals)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{int(s.totalAttempts)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{int(s.totalDeclined)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{int(s.totalTransactions)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{money(s.totalSurcharge)}</TableCell>
            <TableCell />
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{money(s.totalOwed)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{money(s.totalUnallocated)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
}
